<template>
  <Dropdown
    ref="dropdown"
    class="list-dropdown"
    :opened="opened"
    :class="containerClass"
    :disabled="disabled"
    @toggle="onToggle"
    @clickaway="onClickaway"
  >
    <template #caption>
      <slot
        v-if="labelOnSelection && hasSelection"
        name="selection"
        :selection="selection"
      >
        {{ selectedLabels }}
      </slot>
      <slot
        v-else
        name="placeholder"
      >
        {{ placeholder }}
      </slot>
    </template>

    <TextInput
      v-if="hasSearch"
      ref="searchInput"
      v-model="searchInput"
      placeholder="Produkt suchen"
      class="list-dropdown__search-input"
      @input.native="updateSearchResults"
    />

    <div class="list-dropdown__content">
      <ul class="list-dropdown__items">
        <DropdownItem
          v-if="includeBlank"
          :value="null"
          :selected="!hasSelection"
          :label="includeBlank"
          :url="buildItemUrl(null)"
          @click="selectAndMaybeChange"
        />

        <template v-for="(option, index) in filteredOptions">
          <li
            v-if="
              groupEntries &&
              (index === 0 || filteredOptions[index - 1].groupName !== option.groupName)
            "
            :key="`groupname_${index}`"
            class="list-dropdown__item-group-name"
          >
            {{ option.groupName }}
          </li>
          <DropdownItem
            :key="index"
            :label="option.label"
            :value="option.value"
            :count="option.count"
            :indentation="option.depth"
            :selected="isSelected(option.value)"
            :url="buildItemUrl(option.value)"
            @click="selectAndMaybeChange"
          >
            <template #beforeLabel>
              <slot
                name="beforeLabel"
                :option="option"
              />
            </template>
          </DropdownItem>
        </template>
      </ul>
    </div>

    <template #submit>
      <LinkButton
        v-if="multiple"
        tag="button"
        type="button"
        primary
        :disabled="!hasChanged"
        @click="change"
      >
        {{ buttonTextOrDefault }}
      </LinkButton>
    </template>
  </Dropdown>
</template>

<script>
import { isEqual, isNull, isUndefined, uniq, without, debounce } from 'lodash-es';
import Dropdown from '@/components/dropdown.vue';
import DropdownItem from '@/components/dropdown-item.vue';
import { TextInput, LinkButton } from '@i22-td-smarthome/component-library';
import { filteredOptions } from '@/components/filter/list-dropdown';

const DEFAULT_BUTTON_TEXT = 'Übernehmen';

function sanitizeValue(value, multiple) {
  if (isUndefined(value) || isNull(value)) {
    if (multiple) return [];
    return null;
  }
  return value;
}

export default {
  name: 'ListDropdown',
  components: {
    Dropdown,
    DropdownItem,
    TextInput,
    LinkButton,
  },
  props: {
    placeholder: {
      type: String,
      default: null,
    },
    value: {
      type: null,
      default: null,
    },
    options: {
      type: Array,
      required: true,
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    includeBlank: {
      type: String,
      default: null,
    },
    labelOnSelection: {
      type: Boolean,
      default: true,
    },
    itemUrl: {
      type: Function,
      default: null,
    },
    buttonText: {
      type: String,
      default: null,
    },
    opened: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    hasSearch: {
      type: Boolean,
    },
    groupEntries: {
      type: Boolean, // show "group" as header
    },
  },
  emits: ['unselect', 'select', 'input', 'change'],
  data() {
    return {
      internalValue: sanitizeValue(this.value, this.multiple),
      searchInput: '',
      debouncedSearchInput: '',
      searchResults: this.options,
      updateSearchResults: debounce(() => {
        this.debouncedSearchInput = this.searchInput;
      }, 200),
    };
  },
  computed: {
    selection() {
      const filterFunc = (option) => this.isSelected(option.value);
      if (this.multiple) return this.options.filter(filterFunc);
      return this.options.find(filterFunc);
    },
    hasSelection() {
      if (!this.multiple) return !!this.selection;
      return this.selection.length > 0;
    },
    selectedLabels() {
      if (!this.hasSelection) return '';
      if (!this.multiple) return this.selection.label;
      return this.selection
        .map((option) => option.label)
        .sort()
        .join(', ');
    },
    selectedValues() {
      if (this.multiple) return this.internalValue;
      if (this.internalValue === null) return [];
      return [this.internalValue];
    },
    hasChanged() {
      return !isEqual(this.value, this.internalValue);
    },
    containerClass() {
      return {
        'dropdown--simple': !this.multiple,
        'dropdown--multi': this.multiple,
      };
    },
    buttonTextOrDefault() {
      return this.buttonText || DEFAULT_BUTTON_TEXT;
    },
    filteredOptions() {
      return filteredOptions(this.debouncedSearchInput, this.options, this.groupEntries);
    },
  },
  watch: {
    value(value) {
      this.internalValue = sanitizeValue(value, this.multiple);
    },
    opened(isOpened) {
      this.isOpened = isOpened;
      if (isOpened && this.$refs.searchInput) this.$refs.searchInput.focus();
    },
  },
  methods: {
    onToggle(isOpened) {
      if (!isOpened) this.reset();
      if (isOpened && this.$refs.searchInput) this.$refs.searchInput.focus();
    },
    onClickaway() {
      this.reset();
    },
    open() {
      this.$refs.dropdown.open();
    },
    close() {
      this.$refs.dropdown.close();
    },
    select(value) {
      if (value === undefined) {
        this.internalValue = this.multiple ? [] : null;
      } else if (this.multiple) {
        this.internalValue = uniq([...this.internalValue, value]);
      } else {
        this.internalValue = value;
      }
      this.$emit('select', value, this.internalValue);
    },
    unselect(value) {
      if (this.multiple) {
        this.internalValue = without(this.internalValue, value);
      } else if (this.internalValue === value) {
        this.internalValue = null;
      }
      this.$emit('unselect', value, this.internalValue);
    },
    toggleSelection(value) {
      if (this.isSelected(value)) {
        this.unselect(value);
      } else {
        this.select(value);
      }
    },
    unselectAll() {
      if (this.multiple) {
        this.internalValue = [];
      } else {
        this.internalValue = null;
      }
    },
    isSelected(value) {
      return this.selectedValues.some((selectedValue) => isEqual(selectedValue, value));
    },
    change() {
      this.$emit('input', this.internalValue);
      this.$emit('change', this.selection);
      this.close();
    },
    selectAndMaybeChange(value) {
      if (isUndefined(value) || isNull(value)) {
        this.unselectAll();
      } else if (this.multiple) {
        this.toggleSelection(value);
      } else {
        this.select(value);
      }
      if (!this.multiple) this.change();
    },
    reset() {
      this.internalValue = this.value;
    },
    buildItemUrl(value) {
      if (!this.itemUrl || !value) return undefined;
      return this.itemUrl(value);
    },
  },
};
</script>

<style lang="scss" scoped>
@use 'assets/base' as *;

.list-dropdown {
  &__items {
    display: block;
    max-height: 296px;
    overflow-x: hidden;
    overflow-y: auto;
    width: 100%;

    .dropdown--multi & {
      border-bottom: 0;
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
    }
  }

  &__item-group-name {
    @include font-size(15px, 18px);
    font-weight: 700;
    position: sticky;
    top: 0;
    background: var(--color-background-subtle);
    color: var(--color-font-subtle);
    padding: $grid-base * 0.5;
    z-index: 1;
    margin-bottom: -1px;
  }

  &__search-input {
    margin: $grid-base;
  }

  &__submit {
    border-top: 1px solid var(--color-stroke);
    padding: $grid-base;

    .btn {
      width: 100%;
    }
  }
}
</style>
